export enum FlagScope {
  DOBOX_EXECUTOR = 'DOBOX_EXECUTOR',
  DECISIONS = 'DECISIONS',
}

/** Raw stored config state — distinct from ExecutorMode (doboxExecutor.ts),
 *  the resolved per-request mode a run actually sees. ALLOWLIST and ON never
 *  shadow anyone; only SHADOW samples at shadowSampleRate. */
export enum DoBoxExecutorConfigMode {
  OFF = 'OFF',
  SHADOW = 'SHADOW',
  ALLOWLIST = 'ALLOWLIST',
  ON = 'ON',
}

export enum FeatureMode {
  OFF = 'OFF',
  ALLOWLIST = 'ALLOWLIST',
  ON = 'ON',
}

export enum ExecutorModelChoice {
  SONNET = 'SONNET',
  HAIKU = 'HAIKU',
  AB = 'AB',
}

export enum DoBoxExecutorFeature {
  CONFIRMED_SEND_ACTIVITY = 'CONFIRMED_SEND_ACTIVITY',
  EMAIL_UNDO = 'EMAIL_UNDO',
  CAPABILITY_GAPS = 'CAPABILITY_GAPS',
  CODE_DELEGATION = 'CODE_DELEGATION',
  PLAN_V4 = 'PLAN_V4',
  PLAN_DELEGATE_CODE = 'PLAN_DELEGATE_CODE',
  PREPARE = 'PREPARE',
  MEMORY_CAPTURE = 'MEMORY_CAPTURE',
  MEMORY_RECALL = 'MEMORY_RECALL',
  MEMORY_FANOUT = 'MEMORY_FANOUT',
  TEMPLATE_SCOPE = 'TEMPLATE_SCOPE',
  EXECUTE_TASK_TYPE = 'EXECUTE_TASK_TYPE',
  CONTINUATION = 'CONTINUATION',
  HAIKU_EXECUTOR = 'HAIKU_EXECUTOR',
  STANDING_PERMISSIONS = 'STANDING_PERMISSIONS',
  AGENT_ROUTES = 'AGENT_ROUTES',
  AGENT_ROUTES_SUGGEST = 'AGENT_ROUTES_SUGGEST',
  ROUTE_MINER = 'ROUTE_MINER',
  PROCESS_DOBOX_CACHED_TEMPLATE = 'PROCESS_DOBOX_CACHED_TEMPLATE',
  PROCESS_DOBOX_SIGNALS = 'PROCESS_DOBOX_SIGNALS',
  /** Lists a chat conversation's recent system-closed suggestions to
   *  PROCESS_DOBOX, so a message that shows the work is still owed reopens one. */
  PROCESS_DOBOX_CHAT_REOPEN = 'PROCESS_DOBOX_CHAT_REOPEN',
  SLACK_AUTHOR_HMAC = 'SLACK_AUTHOR_HMAC',
  CHAT_COUNTERPART_DIGESTS = 'CHAT_COUNTERPART_DIGESTS',
  /** Gates linking a non-actionable follow-up to an open DoBox of the same
   *  person, and the app-user digests that make that person matchable. */
  FOLLOW_UP_APPEND = 'FOLLOW_UP_APPEND',
  EXECUTOR_ATTENTION_PUSH = 'EXECUTOR_ATTENTION_PUSH',
  EXECUTOR_EXPIRY_WARNING = 'EXECUTOR_EXPIRY_WARNING',
  SCHEDULED_EMAIL_LEDGER = 'SCHEDULED_EMAIL_LEDGER',
  SCHEDULED_EMAIL_REBIND = 'SCHEDULED_EMAIL_REBIND',
  SCHEDULED_EMAIL_SEAL_WIDENING = 'SCHEDULED_EMAIL_SEAL_WIDENING',
  LOCAL_JOB_WATCHER = 'LOCAL_JOB_WATCHER',
  REPLY_WAIT = 'REPLY_WAIT',
  GITHUB_PR_HOOK = 'GITHUB_PR_HOOK',
  LEGACY_DIRECT_SEND_DECONFLICT = 'LEGACY_DIRECT_SEND_DECONFLICT',
  SUGGESTION_AUTO_ACCEPT = 'SUGGESTION_AUTO_ACCEPT',
  MCP_ANNOTATIONS = 'MCP_ANNOTATIONS',
  SEND_AS = 'SEND_AS',
  APPROVAL_GATE_ORIGIN_LINK = 'APPROVAL_GATE_ORIGIN_LINK',
  FANOUT_BM25 = 'FANOUT_BM25',
  /** Gates writes of the AgentDelegation enum values this contract adds,
   *  until the production GraphQL API actually serves them — keeps a worker
   *  deployed ahead of the API from writing a value the API can't read back. */
  DELEGATION_ENUM_WRITES = 'DELEGATION_ENUM_WRITES',
  /** Gates the corrected propose_custom_agent vocabulary on the legacy
   *  (non-executor) path only — no global behaviour change. */
  LEGACY_PROMPT_FIX = 'LEGACY_PROMPT_FIX',
  /** Gates the in-item "create an agent" wizard (suggestion/chat entry
   *  points, the 3-step panel, trial-and-approve) end to end. Fails closed:
   *  absent/OFF means none of that behaviour runs. */
  AGENT_SETUP = 'AGENT_SETUP',
  /** Gates the executor's resolve_person tool and the PERSON_IDENTITY question
   *  that remembers which person a name means. Fails closed. */
  PEOPLE_DIRECTORY = 'PEOPLE_DIRECTORY',
  /** Gates messaging a resolved person on Slack (compose_outbound `person`, the
   *  template's slackPeopleScope). Also requires PEOPLE_DIRECTORY. Fails closed. */
  PEOPLE_MESSAGING = 'PEOPLE_MESSAGING',
  /** Gates the WRITE of `InboxEntry.agentRunHistory` (archiving the outgoing
   *  delegation on replace/end). Global: only ON enables it. Fails closed, so
   *  it stays OFF until every stack that reads InboxEntries tolerates the
   *  `agentRunHistoryJson`/`agentRunHistoryRev` attributes. Reads are never gated. */
  AGENT_RUN_HISTORY = 'AGENT_RUN_HISTORY',
}

export const RUNTIME_FLAG_CONFIG_ENTRY_KEY = 'CONFIG';
export const RUNTIME_FLAG_USER_ENTRY_PREFIX = 'USER#';

/**
 * The single config row for a FlagScope — entryKey is always
 * RUNTIME_FLAG_CONFIG_ENTRY_KEY. Allowlist membership is a separate row
 * shape, RuntimeFlagUserRow, keyed by RUNTIME_FLAG_USER_ENTRY_PREFIX + userId.
 */
export interface DoBoxExecutorConfigRow {
  flagScope: FlagScope;
  entryKey: string;
  mode: DoBoxExecutorConfigMode;
  /** 0..1, default 0. Read only when mode is SHADOW — ALLOWLIST and ON never
   *  shadow anyone, so a stale non-zero rate left over from a prior SHADOW
   *  period is harmless once mode moves on. */
  shadowSampleRate: number;
  features: Partial<Record<DoBoxExecutorFeature, FeatureMode>>;
  executorModel: ExecutorModelChoice;
  haikuAbPercent: number;
  updatedAt: number;
}

export interface RuntimeFlagUserRow {
  flagScope: FlagScope;
  entryKey: string;
  /** Readable: userId is this row's sort key and a GSI key (argument 1 —
   *  allowlist membership must stay queryable both ways). */
  userId: string;
  addedAt: number;
}
